import { useEffect, useMemo, useState } from "react";
import {
  Braces,
  Copy,
  Edit3,
  FileText,
  Paperclip,
  Plus,
  Search,
  Sparkles,
  Trash2,
  X
} from "lucide-react";
import type { ComposerSeed } from "../components/Composer";
import type { EmailAccount, EmailTemplate, Role, User } from "../types";
import { api } from "../lib/api";
import { formatDateTime, mergePreview } from "../lib/format";
import RichTextEditor from "../components/RichTextEditor";
import { Badge, Button, EmptyState, IconButton, Loading, Modal, Switch } from "../components/ui";

const blankTemplate = {
  name: "",
  category: "General",
  subjectTemplate: "",
  bodyHtmlTemplate: "<p>Hello {{CustomerName}},</p><p><br></p><p>Warmly,<br>{{StaffName}}</p>",
  description: "",
  isActive: true,
  roleIds: [] as string[],
  accountIds: [] as string[]
};

export default function TemplatesPage({
  user,
  onCompose,
  notify,
  onChanged
}: {
  user: User;
  onCompose: (seed?: ComposerSeed) => void;
  notify: (tone: "success" | "error" | "info", title: string, message?: string) => void;
  onChanged: () => void;
}) {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [editing, setEditing] = useState<EmailTemplate | null | "new">(null);
  const [form, setForm] = useState(blankTemplate);
  const [saving, setSaving] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [templateFiles, setTemplateFiles] = useState<File[]>([]);

  const load = async () => {
    setLoading(true);
    try {
      const templateResult = await api.templates();
      setTemplates(templateResult);
      if (user.isAdministrator) {
        const [roleResult, accountResult] = await Promise.all([api.roles(), api.accounts()]);
        setRoles(roleResult);
        setAccounts(accountResult);
      }
    } catch (error) {
      notify("error", "Couldn’t load templates", (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const categories = useMemo(
    () => ["All", ...Array.from(new Set(templates.map((template) => template.category)))],
    [templates]
  );
  const filtered = templates.filter(
    (template) =>
      (category === "All" || template.category === category) &&
      `${template.name} ${template.description} ${template.subjectTemplate}`
        .toLowerCase()
        .includes(search.toLowerCase())
  );

  const openEditor = (template?: EmailTemplate) => {
    setTemplateFiles([]);
    if (template) {
      setEditing(template);
      setForm({
        name: template.name,
        category: template.category,
        subjectTemplate: template.subjectTemplate,
        bodyHtmlTemplate: template.bodyHtmlTemplate,
        description: template.description,
        isActive: template.isActive,
        roleIds: template.roleIds ?? [],
        accountIds: template.accountIds ?? []
      });
    } else {
      setEditing("new");
      setForm(blankTemplate);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      let saved: EmailTemplate;
      if (editing === "new") {
        saved = await api.createTemplate(form);
      } else if (editing) {
        saved = await api.updateTemplate(editing.id, form);
      } else {
        return;
      }
      for (const file of templateFiles) saved = await api.uploadTemplateAttachment(saved.id, file);
      notify("success", editing === "new" ? "Template created" : "Template updated",
        templateFiles.length ? `${templateFiles.length} default attachment(s) saved.` : undefined);
      setEditing(null);
      setTemplateFiles([]);
      await load();
      onChanged();
    } catch (error) {
      notify("error", "Couldn’t save template", (error as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const removeDefaultAttachment = async (attachmentId: string) => {
    if (!editing || editing === "new") return;
    try {
      await api.removeTemplateAttachment(editing.id, attachmentId);
      setEditing({ ...editing, defaultAttachments: (editing.defaultAttachments ?? []).filter((item) => item.id !== attachmentId) });
      notify("success", "Default attachment removed");
    } catch (error) {
      notify("error", "Couldn’t remove attachment", (error as Error).message);
    }
  };

  const remove = async (template: EmailTemplate) => {
    if (!window.confirm(`Delete “${template.name}”? Existing sent emails will not be changed.`)) return;
    try {
      await api.deleteTemplate(template.id);
      notify("success", "Template deleted");
      await load();
      onChanged();
    } catch (error) {
      notify("error", "Couldn’t delete template", (error as Error).message);
    }
  };

  return (
    <div className="page templates-page">
      <div className="page-heading">
        <div>
          <span className="eyebrow">Reusable messages</span>
          <h1>Email templates</h1>
          <p>Create polished starting points that your team can personalize before sending.</p>
        </div>
        {user.isAdministrator && (
          <Button icon={<Plus size={17} />} onClick={() => openEditor()}>
            New template
          </Button>
        )}
      </div>

      <div className="content-toolbar">
        <label className="search-field">
          <Search size={16} />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search templates"
          />
        </label>
        <div className="filter-pills">
          {categories.map((item) => (
            <button
              className={category === item ? "active" : ""}
              key={item}
              onClick={() => setCategory(item)}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <Loading label="Loading templates…" />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<FileText />}
          title="No matching templates"
          message="Try a different search or create a fresh template."
          action={
            user.isAdministrator ? (
              <Button icon={<Plus size={16} />} onClick={() => openEditor()}>
                Create template
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="template-grid">
          {filtered.map((template) => (
            <article className="template-card" key={template.id}>
              <header>
                <span className="template-icon">
                  <Sparkles size={18} />
                </span>
                <div>
                  <Badge tone="violet">{template.category}</Badge>
                  {!template.isActive && <Badge tone="warning">Paused</Badge>}
                </div>
                {user.isAdministrator && (
                  <div className="card-actions">
                    <IconButton label="Edit template" onClick={() => openEditor(template)}>
                      <Edit3 size={16} />
                    </IconButton>
                    <IconButton label="Delete template" onClick={() => remove(template)}>
                      <Trash2 size={16} />
                    </IconButton>
                  </div>
                )}
              </header>
              <h2>{template.name}</h2>
              <p>{template.description || "A reusable starting point for your team."}</p>
              <div className="template-subject">
                <small>Subject</small>
                <strong>{mergePreview(template.subjectTemplate)}</strong>
              </div>
              <div className="template-preview" dangerouslySetInnerHTML={{ __html: template.bodyHtmlTemplate }} />
              {(template.defaultAttachments?.length ?? 0) > 0 && (
                <div className="template-file-count">
                  <Paperclip size={14} /> {template.defaultAttachments!.length} default attachment(s)
                </div>
              )}
              <footer>
                <span>Updated {formatDateTime(template.updatedAt)}</span>
                <Button
                  tone="secondary"
                  icon={<Copy size={15} />}
                  disabled={!template.isActive}
                  onClick={() =>
                    onCompose({
                      subject: template.subjectTemplate,
                      bodyHtml: template.bodyHtmlTemplate,
                      templateId: template.id
                    })
                  }
                >
                  Use template
                </Button>
              </footer>
            </article>
          ))}
        </div>
      )}

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "Create template" : "Edit template"}
        subtitle="The copy inserted into a new email always remains editable."
        wide
      >
        <div className="template-editor-layout">
          <div className="template-editor-fields">
            <div className="form-grid two">
              <label className="field">
                <span>Template name</span>
                <input
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                  placeholder="Reservation confirmation"
                />
              </label>
              <label className="field">
                <span>Category</span>
                <input
                  value={form.category}
                  onChange={(event) => setForm({ ...form, category: event.target.value })}
                  placeholder="Reservations"
                />
              </label>
            </div>
            <label className="field">
              <span>Description</span>
              <input
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                placeholder="Tell your team when to use this template"
              />
            </label>
            <label className="field">
              <span>Subject line</span>
              <div className="input-with-end-icon">
                <input
                  value={form.subjectTemplate}
                  onChange={(event) => setForm({ ...form, subjectTemplate: event.target.value })}
                  placeholder="Your stay — {{ReservationNumber}}"
                />
                <Braces size={16} />
              </div>
            </label>
            <div className="field">
              <span>Message body</span>
              <RichTextEditor
                value={form.bodyHtmlTemplate}
                onChange={(bodyHtmlTemplate) => setForm({ ...form, bodyHtmlTemplate })}
                minHeight={260}
              />
            </div>
            <div className="field template-attachments">
              <span>Default attachments</span>
              <p>These files are copied into every message sent with this template.</p>
              <label className="button button-secondary attachment-picker">
                <Paperclip size={16} /> Choose files
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={(event) => setTemplateFiles((current) => [
                    ...current, ...Array.from(event.target.files ?? [])
                  ].slice(0, 10))}
                />
              </label>
              {((editing && editing !== "new" ? editing.defaultAttachments : []) ?? []).map((attachment) => (
                <div className="template-file-row" key={attachment.id}>
                  <FileText size={16} />
                  <span>{attachment.fileName}</span>
                  <IconButton label={`Remove ${attachment.fileName}`} onClick={() => removeDefaultAttachment(attachment.id)}>
                    <X size={14} />
                  </IconButton>
                </div>
              ))}
              {templateFiles.map((file, index) => (
                <div className="template-file-row pending" key={`${file.name}-${index}`}>
                  <FileText size={16} />
                  <span>{file.name}</span>
                  <IconButton label={`Remove ${file.name}`} onClick={() =>
                    setTemplateFiles(templateFiles.filter((_, itemIndex) => itemIndex !== index))}>
                    <X size={14} />
                  </IconButton>
                </div>
              ))}
            </div>
            <fieldset className="role-checklist">
              <legend>Restrict to roles (leave blank for all roles)</legend>
              {roles.map((role) => (
                <label key={role.id}>
                  <input
                    type="checkbox"
                    checked={form.roleIds.includes(role.id)}
                    onChange={(event) => setForm({ ...form, roleIds: event.target.checked
                      ? [...form.roleIds, role.id] : form.roleIds.filter((id) => id !== role.id) })}
                  />
                  <span><strong>{role.name}</strong><small>{role.description}</small></span>
                </label>
              ))}
            </fieldset>
            <fieldset className="role-checklist">
              <legend>Restrict to sending accounts (leave blank for all)</legend>
              {accounts.map((account) => (
                <label key={account.id}>
                  <input
                    type="checkbox"
                    checked={form.accountIds.includes(account.id)}
                    onChange={(event) => setForm({ ...form, accountIds: event.target.checked
                      ? [...form.accountIds, account.id] : form.accountIds.filter((id) => id !== account.id) })}
                  />
                  <span><strong>{account.displayName}</strong><small>{account.emailAddress}</small></span>
                </label>
              ))}
            </fieldset>
            <div className="switch-row">
              <div>
                <strong>Available to the team</strong>
                <span>Paused templates remain saved but cannot be selected.</span>
              </div>
              <Switch
                checked={form.isActive}
                onChange={(isActive) => setForm({ ...form, isActive })}
                label="Template availability"
              />
            </div>
          </div>
          <aside className="template-live-preview">
            <span className="eyebrow">Live preview</span>
            <h3>{mergePreview(form.subjectTemplate) || "Your subject appears here"}</h3>
            <div
              className="email-body"
              dangerouslySetInnerHTML={{ __html: mergePreview(form.bodyHtmlTemplate) }}
            />
          </aside>
        </div>
        <div className="modal-footer">
          <Button tone="secondary" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? "Saving…" : editing === "new" ? "Create template" : "Save changes"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}


